import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

interface ProfileSection {
  id: string;
  label: string;
  path?: string;
}

interface ProfileSectionContextType {
  activeId: string | null;
  setActiveId: (id: string) => void;
  registerSection: (section: ProfileSection) => void;
  unregisterSection: (id: string) => void;
  sections: ProfileSection[];
}

const ProfileSectionContext = createContext<
  ProfileSectionContextType | undefined
>(undefined);

export const useProfileSection = () => {
  const context = useContext(ProfileSectionContext);
  if (!context) {
    throw new Error(
      "useProfileSection must be used within a ProfileSectionProvider"
    );
  }
  return context;
};

interface ProfileSectionProviderProps {
  children: React.ReactNode;
}

export const ProfileSectionProvider: React.FC<ProfileSectionProviderProps> = ({
  children,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [sections, setSections] = useState<ProfileSection[]>([]);
  const sectionsRef = useRef<Map<string, ProfileSection>>(new Map());

  const registerSection = useCallback((section: ProfileSection) => {
    sectionsRef.current.set(section.id, section);
    setSections(Array.from(sectionsRef.current.values()));
  }, []);

  const unregisterSection = useCallback((id: string) => {
    sectionsRef.current.delete(id);
    setSections(Array.from(sectionsRef.current.values()));
  }, []);

  return (
    <ProfileSectionContext.Provider
      value={{
        activeId,
        setActiveId,
        registerSection,
        unregisterSection,
        sections,
      }}
    >
      {children}
    </ProfileSectionContext.Provider>
  );
};

export default ProfileSectionProvider;
